import React, { useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericDateTimeRangePicker from "@ui-components/DateTime/GenericDateTimeRangePicker";
import {
  GenericTable,
  GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { SkeletonTableRows } from "@ui-components/Skeleton/GenericSkeleton";
import { useAppSelector } from "@redux/hooks";
import { parseAxiosError } from "@shared/components/utils/apiErrorParser";
import {
  AXIS_TICK,
  GRID_STROKE,
  RTT_COLOR,
  TOOLTIP_CONTENT_STYLE,
  TOOLTIP_LABEL_STYLE,
} from "kontrolEdge/components/InternetIpMonitoring/historyChart";
import { HealthCheckMetric, metricLabel } from "./HealthCheckData";
import {
  CpuHistoryRecord,
  FullHistoryRecord,
  HealthCheckHistoryParams,
  HealthCheckSeries,
  MemoryHistoryRecord,
  ProcessSnapshot,
  fetchHealthCheckHistory,
} from "./request/request";

interface HealthCheckHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  instance: { id: string; name: string };
  metric: HealthCheckMetric;
}

// The range picker works in whole days and the API returns the newest rows first;
// 300 covers a full day at the 5-minute report rate, so any hour of it is reachable
const LIMIT_OPTIONS = ["10", "25", "50", "100", "300"];
const DEFAULT_LIMIT = "25";
const DAY_MS = 24 * 60 * 60 * 1000;
const BYTES_PER_GB = 1024 ** 3;

const PROCESS_LABELS: Record<string, string> = {
  process_bgp: "BGP",
  process_sync: "Sync daemon",
  process_ha1: "HA client",
  process_ha2: "HA server",
};
const PROCESS_KEYS = Object.keys(PROCESS_LABELS);
const DEFAULT_PROCESS_LABEL = PROCESS_LABELS.process_bgp;

const SERIES_BY_METRIC: Partial<Record<HealthCheckMetric, HealthCheckSeries>> =
  {
    cpu: "cpu",
    ram: "ram",
    storage: "storage",
  };

interface DateRange {
  start: Date | null;
  end: Date | null;
}

interface ChartPoint {
  time: string;
  percent: number;
  usedGb?: number;
  totalGb?: number;
}

interface ProcessRow {
  time: string;
  up: boolean;
  cpu: number;
  memory: number;
  uptime: string;
}

interface PortRow {
  time: string;
  statuses: Record<string, string>;
}

type HistoryState<T> =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; rows: T[] };

const defaultRange = (): DateRange => {
  const end = new Date();
  return { start: new Date(end.getTime() - DAY_MS), end };
};

const formatTime = (iso: string): string =>
  new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

const toGb = (bytes: number): number => bytes / BYTES_PER_GB;

// The API answers newest-first; charts and tables read better oldest-first / newest-first respectively.
const toChartPoints = (
  records: (CpuHistoryRecord | MemoryHistoryRecord)[],
): ChartPoint[] =>
  [...records].reverse().map((record) => {
    const time = formatTime(record.createdAt);
    if ("cpu" in record) return { time, percent: record.cpu };
    const usage = "ram" in record ? record.ram : record.storage;
    return {
      time,
      percent: usage.percent,
      usedGb: toGb(usage.used),
      totalGb: toGb(usage.total),
    };
  });

const isProcessSnapshot = (
  value: ProcessSnapshot | string | undefined,
): value is ProcessSnapshot => typeof value === "object" && value !== null;

// A process that went down arrives without `execution_times` (and with 0/0 usage),
// so the presence of that field is what tells Up from Down.
const toProcessRow = (
  record: FullHistoryRecord,
  processKey: string,
): ProcessRow | null => {
  const snapshot = record.processStatus?.[processKey];
  if (!isProcessSnapshot(snapshot)) return null;
  return {
    time: formatTime(record.createdAt),
    up: Boolean(snapshot.execution_times),
    cpu: snapshot.cpu_percent,
    memory: snapshot.memory_percent,
    uptime: snapshot.execution_times ?? "—",
  };
};

const toPortRow = (record: FullHistoryRecord): PortRow => ({
  time: formatTime(record.createdAt),
  statuses: Object.fromEntries(
    Object.entries(record.ports ?? {}).map(([port, { status }]) => [
      port,
      status,
    ]),
  ),
});

const statusTag = (isUp: boolean) => (
  <GenericTag
    label={isUp ? "UP" : "Down"}
    variant={isUp ? "success" : "error"}
    size="small"
  />
);

const PROCESS_COLUMNS: GenericTableColumn<ProcessRow>[] = [
  { header: "Time", renderCell: (row) => row.time },
  { header: "Status", renderCell: (row) => statusTag(row.up) },
  { header: "CPU %", renderCell: (row) => row.cpu },
  { header: "Memory %", renderCell: (row) => row.memory },
  { header: "Uptime", renderCell: (row) => row.uptime },
];

const buildPortColumns = (ports: string[]): GenericTableColumn<PortRow>[] => [
  { header: "Time", renderCell: (row) => row.time },
  ...ports.map((port) => ({
    header: `Port ${port}`,
    renderCell: (row: PortRow) => {
      const status = row.statuses[port];
      if (!status)
        return <GenericTag label="No data" variant="neutral" size="small" />;
      return statusTag(status.toUpperCase() === "UP");
    },
  })),
];

const BodyMessage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex h-[260px] items-center justify-center text-sm text-semantic-text-secondary">
    {children}
  </div>
);

const HistoryChart: React.FC<{
  points: ChartPoint[];
  metric: HealthCheckMetric;
}> = ({ points, metric }) => (
  <div className="h-[300px] w-full">
    <ResponsiveContainer width="100%" height="100%">
      <LineChart
        data={points}
        margin={{ top: 16, right: 24, left: 8, bottom: 8 }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          vertical={false}
          stroke={GRID_STROKE}
        />
        <XAxis
          dataKey="time"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tick={AXIS_TICK}
          interval="preserveStartEnd"
        />
        <YAxis
          domain={[0, 100]}
          tickFormatter={(value: number) => `${value}%`}
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tick={AXIS_TICK}
        />
        <Tooltip
          // The shared style sets no background, so Recharts paints white and the label vanishes in dark mode
          contentStyle={{ ...TOOLTIP_CONTENT_STYLE, backgroundColor: "var(--color-semantic-bg-primary)" }}
          labelStyle={TOOLTIP_LABEL_STYLE}
          formatter={(value: number, _name, item) => {
            const { usedGb, totalGb } = item.payload as ChartPoint;
            const detail =
              usedGb !== undefined && totalGb !== undefined
                ? ` (${usedGb.toFixed(2)} / ${totalGb.toFixed(2)} GB)`
                : "";
            return [`${value}%${detail}`, metricLabel(metric)];
          }}
        />
        <Line
          type="monotone"
          dataKey="percent"
          name={metricLabel(metric)}
          stroke={RTT_COLOR}
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 4 }}
        />
      </LineChart>
    </ResponsiveContainer>
  </div>
);

const HealthCheckHistoryModal: React.FC<HealthCheckHistoryModalProps> = ({
  isOpen,
  onClose,
  instance,
  metric,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const { tenantId } = useAppSelector((state) => state.selection);

  // The parent mounts this modal per opened cell, so state starts at the defaults every time.
  const [range, setRange] = useState<DateRange>(defaultRange);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [processLabel, setProcessLabel] = useState(DEFAULT_PROCESS_LABEL);
  const [state, setState] = useState<HistoryState<unknown>>({
    kind: "loading",
  });

  const { start, end } = range;

  useEffect(() => {
    if (!isOpen || !start || !end) return;
    let cancelled = false;
    setState({ kind: "loading" });

    const params: HealthCheckHistoryParams = {
      tenantId,
      limit: Number(limit),
      startDate: start,
      endDate: end,
      only: SERIES_BY_METRIC[metric],
    };

    const load = async () => {
      try {
        const token = await getAccessTokenSilently();
        const resp = await fetchHealthCheckHistory<unknown>(
          token,
          instance.id,
          params,
        );
        if (!cancelled) setState({ kind: "ready", rows: resp.data.data ?? [] });
      } catch (error) {
        if (!cancelled)
          setState({ kind: "error", message: parseAxiosError(error).message });
      }
    };
    load();

    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    instance.id,
    metric,
    tenantId,
    limit,
    start,
    end,
    getAccessTokenSilently,
  ]);

  const processKey = useMemo(
    () =>
      PROCESS_KEYS.find((key) => PROCESS_LABELS[key] === processLabel) ??
      PROCESS_KEYS[0],
    [processLabel],
  );

  const renderBody = () => {
    if (state.kind === "loading") {
      return (
        <div className="p-4">
          <SkeletonTableRows
            rows={5}
            cols={4}
            rowHeight={15}
            rowGapClassName="space-y-3"
          />
        </div>
      );
    }
    if (state.kind === "error")
      return <BodyMessage>{state.message}</BodyMessage>;
    if (state.rows.length === 0)
      return <BodyMessage>No data for this range.</BodyMessage>;

    // The cast is safe: the request's `only` / absence of it fixes the record shape per metric.
    if (metric === "cpu" || metric === "ram" || metric === "storage") {
      const records = state.rows as (CpuHistoryRecord | MemoryHistoryRecord)[];
      return <HistoryChart points={toChartPoints(records)} metric={metric} />;
    }

    const records = state.rows as FullHistoryRecord[];
    if (metric === "process") {
      const rows = records
        .map((record) => toProcessRow(record, processKey))
        .filter((row): row is ProcessRow => row !== null);
      if (rows.length === 0)
        return <BodyMessage>No data for this range.</BodyMessage>;
      return (
        <GenericTable
          columns={PROCESS_COLUMNS}
          data={rows}
          maxTableBodyHeight="320px"
          isFooterVisible={false}
          initialRowsPerPage={Number(limit)}
          width="100%"
          tableClassName="bg-semantic-bg-primary"
        />
      );
    }

    const rows = records.map(toPortRow);
    const ports = Array.from(
      new Set(rows.flatMap((row) => Object.keys(row.statuses))),
    ).sort((a, b) => Number(a) - Number(b));
    return (
      <GenericTable
        columns={buildPortColumns(ports)}
        data={rows}
        maxTableBodyHeight="320px"
        isFooterVisible={false}
        initialRowsPerPage={Number(limit)}
        width="100%"
        tableClassName="bg-semantic-bg-primary"
      />
    );
  };

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={`${metricLabel(metric)} history · ${instance.name}`}
    >
      {/* The picker popup is absolutely positioned and the modal body clips it, so the body keeps
            a floor tall enough to contain the calendar and time selectors (no existing modal solves this). */}
      <div className="flex min-h-[520px] flex-col gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <GenericDateTimeRangePicker
            label="Date range"
            value={range}
            onChange={setRange}
            className="min-w-[340px] flex-1"
          />
          <div className="flex flex-col space-y-2">
            <span className="text-sm font-medium text-semantic-text-secondary">Limit</span>
            <GenericDropdown
              items={LIMIT_OPTIONS}
              value={limit}
              onSelect={setLimit}
              widthClass="w-24"
              minWidthClass="min-w-[96px]"
            />
          </div>
          {metric === "process" && (
            <div className="flex flex-col space-y-2">
              <span className="text-sm font-medium text-semantic-text-secondary">Process</span>
              <GenericDropdown
                items={Object.values(PROCESS_LABELS)}
                value={processLabel}
                onSelect={setProcessLabel}
                widthClass="w-40"
              />
            </div>
          )}
        </div>
        {renderBody()}
      </div>
    </GenericModal>
  );
};

export default HealthCheckHistoryModal;
